import { motion } from "framer-motion";
import { Receipt, Hourglass, FileCheck2, ArrowRight } from "lucide-react";
import { WHATSAPP_URL } from "@/lib/constants";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { trackConversion } from "@/lib/tracking";

const ease = [0.16, 1, 0.3, 1];

const points = [
  {
    icon: Receipt,
    title: "Travaux avant 2028",
    body: "Toute rénovation réalisée jusqu'à fin 2027 reste déductible de vos impôts.",
  },
  {
    icon: Hourglass,
    title: "Fenêtre limitée",
    body: "Il vous reste moins de 16 mois pour en profiter avant la suppression définitive.",
  },
  {
    icon: FileCheck2,
    title: "Devis gratuit en 24h",
    body: "Chiffrage clair, ligne par ligne, sans engagement de votre part.",
  },
];

export const Fiscal2028 = () => {
  return (
    <section
      id="fiscal-2028"
      data-testid="fiscal-2028-section"
      className="relative bg-charcoal-deep py-24 sm:py-32 lg:py-36 overflow-hidden"
    >
      {/* Atmospheric glows — same visual language as the QuoteForm section */}
      <div className="absolute -top-40 -left-32 w-[420px] h-[420px] bg-bronze/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -right-32 w-[420px] h-[420px] bg-bronze/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease }}
          className="max-w-3xl"
        >
          <span className="inline-flex items-center gap-2 text-bronze-light text-xs uppercase tracking-[0.25em] font-medium">
            <span className="h-px w-8 bg-bronze-light" />
            Réforme fiscale suisse 2028
          </span>
          <h2 className="mt-6 font-serif text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] font-normal">
            Rénovez avant 2028{" "}
            <span className="italic text-bronze-light">
              et déduisez vos travaux de vos impôts.
            </span>
          </h2>
          <p className="mt-8 text-white/75 text-base lg:text-lg leading-relaxed font-light max-w-2xl">
            Suite au vote suisse du 28 septembre 2025, les déductions fiscales pour
            travaux de rénovation seront supprimées dès 2028. Jusqu'à fin 2027, vous
            pouvez encore déduire vos frais de rénovation de vos impôts. Après cette
            date, cet avantage disparaît définitivement.
          </p>
        </motion.div>

        {/* 3 key points */}
        <div className="mt-16 lg:mt-20 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {points.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.title}
                data-testid={`fiscal-2028-card-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, ease, delay: 0.1 + i * 0.1 }}
                className="group relative bg-white/[0.03] border border-white/10 backdrop-blur-sm p-8 lg:p-10 hover:bg-white/[0.05] hover:border-bronze/40 transition-all duration-500"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-bronze/10 border border-bronze/30 mb-6 group-hover:scale-105 transition-transform duration-500">
                  <Icon className="w-5 h-5 text-bronze-light" strokeWidth={1.5} />
                </div>
                <h3 className="font-serif text-white text-2xl leading-snug font-normal mb-3">
                  {p.title}
                </h3>
                <p className="text-white/65 text-sm leading-relaxed font-light">
                  {p.body}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease, delay: 0.4 }}
          className="mt-16 lg:mt-20 flex flex-col items-center gap-4 text-center"
        >
          <a
            href={WHATSAPP_URL}
            onClick={trackConversion(WHATSAPP_URL)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="fiscal-2028-cta"
            className="group inline-flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#1ebe5a] text-white px-10 py-5 text-xs uppercase tracking-[0.25em] font-medium transition-all duration-300 shadow-[0_8px_24px_rgba(37,211,102,0.35)] hover:shadow-[0_12px_32px_rgba(37,211,102,0.55)]"
          >
            <WhatsAppIcon className="w-5 h-5" />
            Demander mon devis avant 2028
            <ArrowRight
              className="w-4 h-4 transition-transform group-hover:translate-x-1"
              strokeWidth={1.5}
            />
          </a>
          <span className="text-white/50 text-[11px] tracking-[0.25em] uppercase">
            Réponse sous 24h · Sans engagement
          </span>
        </motion.div>
      </div>
    </section>
  );
};
